import { useEffect, useState } from 'react';

export const PLAYER_MODULES_RETRY_MS = 10_000;

export type PlayerModulesState = 'loading' | 'loaded' | 'failed';

function loadPlayerModules() {
  return Promise.all([
    import('src/core/video/components/players/hls/HLSPlayer'),
    import('src/core/video/components/players/hls/ConnectHLSPlayer'),
    import('src/core/video/components/players/rtspSimpleServerPlayer/WebRTCPlayer'),
  ]);
}

/**
 * Load the players that `CameraPlayer` imports lazily, and retry until they load.
 *
 * `CameraPlayer` loads the HLS player only when WebRTC fails, which is often when the network is
 * down. `React.lazy` keeps a failed import forever and `Loadable` then shows its own error screen
 * until the page reloads. Render `CameraPlayer` only after this hook reports `loaded`.
 *
 * @param isOnline Whether the browser reports a network connection. A change triggers a retry.
 * @returns The load state of the player modules.
 */
export function usePlayerModules(isOnline: boolean): PlayerModulesState {
  const [state, setState] = useState<PlayerModulesState>('loading');
  const loaded = state === 'loaded';

  useEffect(() => {
    if (loaded) return;

    let cancelled = false;
    let retryTimer: number | undefined;
    const attempt = () => {
      loadPlayerModules().then(
        () => {
          if (!cancelled) setState('loaded');
        },
        () => {
          if (cancelled) return;
          setState('failed');
          retryTimer = window.setTimeout(attempt, PLAYER_MODULES_RETRY_MS);
        },
      );
    };
    attempt();

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
  }, [loaded, isOnline]);

  return state;
}
